/**
 * Main entry point for Lord of the Swarm.
 *
 * AI models are configured per-faction in the Settings panel and persisted to
 * localStorage. On startup the game starts with heuristic AI for all factions;
 * loadSettings() then calls setFactionAiChoice() for each faction, which loads
 * the selected model on demand via ai_model_catalog.js.
 */

// Must stay the first import: installs the roundRect / findLast fallbacks
// before any module that might use them at top level runs.
import './polyfills.js';
import { Game } from './game.js';
import { loadLevels, getLevelById, getDefaultLevelId } from './levels.js';
import { initAnalytics } from './analytics.js';
import { initTextPanel, openTextPanel } from './text_panel.js';
import { initErrorTracking } from './error_tracking.js';
import { initExternalIntegrations, cgLoadingStart, cgLoadingStop, onCgMuteAudio } from './external_integrations.js';
import { runSaveMigrations } from './storage.js';
import { initPwa, promptInstall, isInstallAvailable, isUpdateReady, applyUpdate } from './pwa.js';
import { applyOutboundLinkPolicy, getInstallOffer } from './platform.js';
// the tom.to word mark: the shared engine, as the corner signature on the splash
import { mountInkMark, SIGNATURE } from 'particle_logo';

// Before window.onload on purpose: the browser can fire `beforeinstallprompt`
// as soon as it has the manifest and worker, and an unhandled one is gone.
initPwa();

// Also before onload: the splash markup is parsed by the time this module
// runs, but onload waits on the artwork, and a player can click the word mark
// in between. Game portals forbid links out of the game — see platform.js.
applyOutboundLinkPolicy();

window.onload = async function () {
    try {
        await boot();
    } catch (err) {
        showBootFailure(err);
    }
};

/**
 * Everything that has to succeed before the player can press Play: integrations,
 * the level data, the game object, and the splash wiring.
 * @returns {Promise<void>}
 */
async function boot() {
    // ?level=<id>     — load a specific level instead of the default
    // ?autostart=1    — skip the splash and start immediately (used by benchmarks)
    const urlParams   = new URLSearchParams(window.location.search);
    const levelParam  = urlParams.get('level');
    const autoStart   = urlParams.has('autostart');

    initTextPanel();
    initErrorTracking();
    initAnalytics();
    // Integrations first: every cg*() call is a no-op until the SDK is wired
    // up, so a cgLoadingStart() before this await would be silently dropped.
    await initExternalIntegrations();
    cgLoadingStart();
    // After integrations so migrations run on the active storage backend,
    // before the Game constructor so nothing reads pre-migration data.
    runSaveMigrations();
    await loadLevels();
    // Pre-fetch the requested level (or default) so the Game constructor has it cached.
    await getLevelById(levelParam || getDefaultLevelId());

    // Create the game but do NOT start the render loop yet — no wasted CPU while
    // the splash home screen is showing.
    const game = new Game({ headless: false, levelId: levelParam || undefined });
    window.game = game;

    // Closing or navigating away mid-level is the most common way a game ends
    // without a win or loss. pagehide (not unload) is the event that still
    // fires when a mobile browser freezes the page into its back/forward
    // cache; a tab the OS kills outright can still get away without it.
    window.addEventListener('pagehide', () => game.trackAbandonment('page_exit'));

    onCgMuteAudio((mute) => {
        if (game.soundManager && game.soundManager.muted !== mute) game.soundManager.toggleMute();
    });

    const splash  = document.getElementById('splash');
    const loading = document.getElementById('splash-loading');
    const actions = document.getElementById('splash-actions');

    if (!splash || autoStart) {
        // No splash in this build, or benchmark/autostart mode — start immediately.
        splash?.remove();
        cgLoadingStop();
        if (game.soundManager) { game.soundManager.enabled = false; game.soundManager.muted = true; }
        game.start();
        game.startGame();
        return;
    }

    // Swap spinner → action buttons (image stays visible throughout).
    if (loading) loading.classList.add('hidden');
    if (actions) actions.classList.add('ready');
    cgLoadingStop();

    // Publish where the artwork actually lands: #splash-img is object-fit:
    // contain, so it is letterboxed by whatever the viewport's aspect ratio
    // leaves over, and the tom.to mark hangs off the artwork's corner rather
    // than the screen's. Registered before the mark mounts so that on a resize
    // this runs first and the engine then measures the slot where it ended up.
    const splashImg = /** @type {HTMLImageElement|null} */ (document.getElementById('splash-img'));
    const measurePoster = () => {
        if (!splashImg?.naturalWidth || !splashImg.naturalHeight) return; // not loaded: CSS fallback stands
        const box = splash.getBoundingClientRect();
        const scale = Math.min(box.width / splashImg.naturalWidth, box.height / splashImg.naturalHeight);
        const w = splashImg.naturalWidth * scale, h = splashImg.naturalHeight * scale;
        splash.style.setProperty('--poster-inset-x', `${Math.max(0, (box.width - w) / 2)}px`);
        splash.style.setProperty('--poster-inset-y', `${Math.max(0, (box.height - h) / 2)}px`);
        splash.style.setProperty('--poster-size', `${Math.min(w, h)}px`);
    };
    measurePoster();
    window.addEventListener('resize', measurePoster);
    if (splashImg && !splashImg.complete) splashImg.addEventListener('load', measurePoster, { once: true });

    // The tom.to word mark in the artwork's corner: a flock of brush strokes
    // that inks the wordmark and scatters from the cursor. WebGL2-only — where
    // it can't boot, the plain-text fallback in the slot stays visible.
    const markSlot   = document.getElementById('splash-mark');
    const markCanvas = /** @type {HTMLCanvasElement|null} */ (document.getElementById('splash-mark-canvas'));
    // SIGNATURE is the preset for a splash: white ink, right-aligned in the
    // slot, a nudge of a flee, settled on load. The engine marks the slot
    // is-live while it runs, which hides the fallback text.
    const inkMark    = markSlot && markCanvas ? mountInkMark({ ...SIGNATURE, wrap: markSlot, canvas: markCanvas }) : null;

    /** Dismiss the splash and enter the game's pause/home screen. */
    const enterGame = (afterFn) => {
        // Unlock audio on this user gesture so iOS AudioContext initialises correctly.
        // enabled must be set to true before unlock() — otherwise unlock() is a no-op.
        if (game.soundManager) { game.soundManager.enabled = true; game.soundManager.unlock(); }
        splash.classList.add('hidden');
        // Keep the flock flying through the fade-out, then hand back its GL
        // context and listeners as the splash leaves the DOM.
        splash.addEventListener('transitionend', () => {
            inkMark?.destroy();
            window.removeEventListener('resize', measurePoster);
            splash.remove();
        }, { once: true });
        // Start the render loop but not the simulation — the in-game play button does that.
        game.start();
        game.enterSplash = null; // splash is gone; no-op any future calls
        afterFn?.();
    };

    // Expose so the global keydown handler (ui.js) can dismiss the splash on Space.
    game.enterSplash = enterGame;

    document.getElementById('splash-play')?.addEventListener('click', () => {
        enterGame();
    }, { once: true });

    document.getElementById('splash-levels')?.addEventListener('click', () => {
        enterGame(() => game.ui?.setLevelChooserOpen(true));
    }, { once: true });

    // Over the splash, not instead of it: the panel dialog sits at the same
    // z-index tier (and later in the DOM), so it covers the splash and closing
    // it lands the player back where they were. Not `once` — a panel can be
    // read more than once.
    document.getElementById('splash-info')?.addEventListener('click', () => {
        openTextPanel('info');
    });

    document.getElementById('splash-privacy')?.addEventListener('click', () => {
        openTextPanel('privacy');
    });

    // A new deploy is picked up only while the player is still on the splash:
    // reloading mid-match to save someone a few seconds of staleness is a bad
    // trade. An update that lands later just stays waiting, and the new worker
    // takes over on the next launch.
    const applyUpdateIfIdle = () => {
        if (document.getElementById('splash')) applyUpdate();
    };
    if (isUpdateReady()) applyUpdateIfIdle();
    window.addEventListener('lots:update-ready', applyUpdateIfIdle);

    renderInstallOffer(getInstallOffer());
}

/**
 * Give up visibly rather than silently.
 *
 * Boot is a chain of network fetches, and on a phone any of them can fail for
 * reasons that have nothing to do with the game — a tunnel, a captive portal,
 * a tab the OS froze mid-request. The fetches retry (see net.js), but when the
 * retries are spent the spinner must not just keep spinning: it says "loading"
 * forever while nothing is loading, and the only way out a player can guess at
 * is closing the tab. So the splash says what happened and offers the one
 * action that actually helps.
 *
 * @param {unknown} err
 * @returns {void}
 */
function showBootFailure(err) {
    console.error('[boot] Startup failed', err);
    // The rejection is caught now, so nothing else will report it.
    try {
        window.Sentry?.captureException?.(err, { tags: { phase: 'boot' } });
    } catch { /* error tracking may itself have failed to load */ }
    // Never leave a portal host waiting on a loading state that will not end.
    try { cgLoadingStop(); } catch { /* integrations may not have initialised */ }

    document.getElementById('splash-loading')?.classList.add('hidden');
    const panel = document.getElementById('splash-error');
    if (!panel) return;
    panel.hidden = false;
    document.getElementById('splash-retry')?.addEventListener('click', () => {
        window.location.reload();
    }, { once: true });
}

/**
 * Show whichever install this surface offers — the browser's own prompt, a
 * store listing, or nothing at all. This switch is the only place on the
 * splash that varies by surface, and it reads the answer rather than working
 * it out: which offer belongs where is platform.js's to decide, including the
 * `none` that keeps store links off game portals.
 *
 * @param {import('./platform.js').InstallOffer} offer
 * @returns {void}
 */
function renderInstallOffer(offer) {
    if (offer.kind === 'play-store' || offer.kind === 'app-store') {
        const link = document.getElementById('splash-store');
        if (!link || !offer.url) return;
        link.href = offer.url;
        link.hidden = false;
        return;
    }

    const installBtn = document.getElementById('splash-install');
    if (!installBtn) return;

    if (offer.kind === 'pwa-instructions') {
        // No event will ever announce this one, so the button is shown straight
        // away and explains the gesture instead of performing it.
        installBtn.hidden = false;
        installBtn.addEventListener('click', () => showInstallHelp(offer.gesture));
        return;
    }

    if (offer.kind !== 'pwa-prompt') return;

    // Hidden until the browser says an install is possible, and gone again once
    // the prompt has been answered either way. Not `once` — a dismissed prompt
    // can be offered again on the next visit.
    // initPwa() runs before this, so the prompt may already be in hand — the
    // event that announced it has been and gone.
    if (isInstallAvailable()) installBtn.hidden = false;
    window.addEventListener('lots:installable', () => { installBtn.hidden = false; });
    window.addEventListener('lots:installed', () => { installBtn.hidden = true; });
    installBtn.addEventListener('click', async () => {
        // A caveat means the prompt fires fine but lands the player in an OS
        // warning. Explain it before that happens, not after: the panel's own
        // button fires the prompt, so the install is still one gesture away.
        if (offer.caveat) { showInstallHelp(offer.caveat); return; }
        installBtn.hidden = true;
        await promptInstall();
    });
}

/**
 * Open the install-instructions panel on the steps for one gesture, and wire
 * its dismissals the first time. Closes on the button, on the backdrop, and on
 * Escape — an iPad with a keyboard is still iOS, and macOS always has one.
 *
 * @param {'ios-home-screen'|'macos-dock'|'android-play-protect'} [gesture]
 *   which steps to show
 * @returns {void}
 */
function showInstallHelp(gesture) {
    const panel = document.getElementById('splash-install-help');
    if (!panel) return;

    let shown = false;
    for (const steps of panel.querySelectorAll('.install-steps')) {
        const match = steps.dataset.gesture === gesture;
        steps.hidden = !match;
        if (!match) continue;
        shown = true;
        const title = document.getElementById('splash-install-help-title');
        if (title) title.textContent = steps.dataset.title ?? 'Install';
    }
    // An offer naming a gesture we have no steps for is a bug, not something to
    // show an empty dialog for.
    if (!shown) return;

    if (!panel.dataset.wired) {
        panel.dataset.wired = '1';
        const close = () => { panel.hidden = true; };
        document.getElementById('splash-install-help-close')
            ?.addEventListener('click', close);
        // The one panel that acts rather than only explaining. This click is
        // the user gesture promptInstall() requires, so it is called straight
        // from the handler — nothing may be awaited ahead of it. Only present
        // on the Samsung block; every other gesture leaves this a no-op.
        document.getElementById('splash-install-anyway')?.addEventListener('click', () => {
            close();
            const installBtn = document.getElementById('splash-install');
            if (installBtn) installBtn.hidden = true;
            void promptInstall();
        });
        // Backdrop only — a tap inside the card must not dismiss it.
        panel.addEventListener('click', (event) => {
            if (event.target === panel) close();
        });
        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && !panel.hidden) close();
        });
    }

    panel.hidden = false;
    document.getElementById('splash-install-help-close')?.focus();
}
